/**
 * FollowButton - 关注按钮组件
 * 
 * 支持关注/取消关注事件
 */

import { useState, useEffect } from 'react'
import WebApp from '@twa-dev/sdk'
import { apiClient } from '../api/client'
import { getAnalyticsContext } from '../utils/analytics'
import { useLanguage } from '../hooks/useLanguage'

interface FollowButtonProps {
  eventId: string
  date: string
  eventTitle: string
  narrativeName: string
  eventInstanceId?: string
  eventData?: Record<string, unknown>
  onFollowChange?: (isFollowing: boolean) => void
  compact?: boolean
}

export function FollowButton({
  eventId,
  date,
  eventTitle,
  narrativeName,
  eventInstanceId,
  eventData,
  onFollowChange,
  compact = false
}: FollowButtonProps) {
  const { t } = useLanguage()
  const [isFollowing, setIsFollowing] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const resolvedEventInstanceId = eventInstanceId || (
    typeof eventData?.event_instance_id === 'string' ? eventData.event_instance_id : ''
  )

  // 检查是否已关注
  useEffect(() => {
    const checkFollowStatus = async () => {
      try {
        const response = await apiClient.get<{ follows: Array<{ event_id: string }> }>(
          '/api/user/follows'
        )
        
        const isEventFollowing = response.follows.some(
          (f) => f.event_id === eventId
        )
        setIsFollowing(isEventFollowing)
      } catch (err) {
        console.error('Failed to check follow status:', err)
      }
    }

    checkFollowStatus()
  }, [eventId])

  // 关注事件
  const handleFollow = async () => {
    setIsLoading(true)

    try {
      const response = await apiClient.post<{ success: boolean; error?: string; message?: string }>(
        '/api/user/follow/event',
        {
          event_id: eventId,
          event_instance_id: resolvedEventInstanceId,
          date: date,
          event_title: eventTitle,
          narrative_name: narrativeName,
          event_data: eventData,
          analytics_context: getAnalyticsContext(),
        }
      )

      if (response.success) {
        setIsFollowing(true)
        WebApp.HapticFeedback.notificationOccurred('success')
        onFollowChange?.(true)
        
        if (response.message === '已关注') {
          WebApp.showAlert(t('您已经关注过这个事件了', 'You are already following this event'))
        } else {
          WebApp.showAlert(t('关注成功！您最多可以关注 3 个事件', 'Followed. You can follow up to 3 events.'))
        }
      } else {
        WebApp.HapticFeedback.notificationOccurred('error')
        WebApp.showAlert(response.error || t('关注失败', 'Failed to follow'))
      }
    } catch (err) {
      console.error('Follow failed:', err)
      WebApp.HapticFeedback.notificationOccurred('error')
      WebApp.showAlert(t('关注失败，请稍后重试', 'Failed to follow. Please try again later.'))
    } finally {
      setIsLoading(false)
    }
  }

  // 取消关注
  const handleUnfollow = async () => {
    setIsLoading(true)

    try {
      const response = await apiClient.delete<{ success: boolean; error?: string; message?: string }>(
        `/api/user/follow/event?${new URLSearchParams({
          event_id: eventId,
          event_instance_id: resolvedEventInstanceId,
          date,
          narrative_name: narrativeName,
        }).toString()}`
      )

      if (response.success) {
        setIsFollowing(false)
        WebApp.HapticFeedback.notificationOccurred('success')
        onFollowChange?.(false)
        WebApp.showAlert(t('已取消关注', 'Unfollowed'))
      } else {
        WebApp.HapticFeedback.notificationOccurred('error')
        WebApp.showAlert(response.error || t('取消关注失败', 'Failed to unfollow'))
      }
    } catch (err) {
      console.error('Unfollow failed:', err)
      WebApp.HapticFeedback.notificationOccurred('error')
      WebApp.showAlert(t('取消关注失败，请稍后重试', 'Failed to unfollow. Please try again later.'))
    } finally {
      setIsLoading(false)
    }
  }

  // 点击处理
  const handleClick = () => {
    WebApp.HapticFeedback.impactOccurred('light')
    
    if (isFollowing) {
      handleUnfollow()
    } else {
      handleFollow()
    }
  }

  return (
    <button
      onClick={handleClick}
      disabled={isLoading}
      className={`
        flex items-center justify-center font-medium transition-all
        ${compact ? 'gap-1 px-2 py-2 rounded-md text-xs' : 'gap-2 px-4 py-2 rounded-lg'}
        ${isFollowing
          ? 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300'
          : 'bg-blue-500 text-white hover:bg-blue-600'
        }
        ${isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
      `}
    >
      {isLoading ? (
        <span className="animate-spin">⏳</span>
      ) : isFollowing ? (
        <>
          <span>📌</span>
          <span>{t('已关注', 'Following')}</span>
        </>
      ) : (
        <>
          <span>📌</span>
          <span>{t('关注事件', 'Follow Event')}</span>
        </>
      )}
    </button>
  )
}
